import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {type Column} from '@/components/data-table'
import {FilterSelect} from '@/components/cartera/filter-select'
import {PaginatedDataTable} from '@/components/cartera/paginated-data-table'
import {facturaBaseColumns} from '@/components/cartera/factura-columns'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  useCarteraFacturas,
  type AdminInvoice,
  type BillingInvoiceEstado,
  type BillingPayerKind,
} from '@/hooks/inci/cartera/use-cartera'
import {SearchInput} from '@/components/ui/search-input'

const ESTADOS: BillingInvoiceEstado[] = ['emitida', 'pagada', 'vencida', 'anulada']
const PAYER_KINDS: BillingPayerKind[] = ['tenant', 'partner']

/** Read-only (docs/modulos/pricing-spec.md §Superficies UI): anular y pago manual viven en el panel de empresa. */
export function FacturasTab() {
  const {t} = useTranslation()
  const table = useTableState('created_at')
  const [estado, setEstado] = useState<BillingInvoiceEstado | ''>('')
  const [payerKind, setPayerKind] = useState<BillingPayerKind | ''>('')
  const {data, isLoading} = useCarteraFacturas({
    page: table.page,
    search: table.search,
    estado,
    payerKind,
  })

  const [numero, estado_, vencimiento, total] = facturaBaseColumns(t)
  const columns: Column<AdminInvoice>[] = [
    numero,
    {
      key: 'empresa',
      header: t('cartera.facturas.table.empresa'),
      width: 'w-52',
      cell: (row) => (
        <Link
          to={`/admin/empresas/${row.business_id}`}
          className="block truncate font-medium text-primary hover:underline"
          data-testid={`cartera-factura-empresa-${row.id}`}>
          {row.business_name}
        </Link>
      ),
    },
    estado_,
    vencimiento,
    total,
  ]

  return (
    <PaginatedDataTable
      testId="cartera-facturas-tab"
      columns={columns}
      data={data}
      isLoading={isLoading}
      page={table.page}
      onPageChange={table.setPage}
      emptyMessage={t('cartera.facturas.empty')}
      toolbar={
        <>
          <SearchInput
            value={table.searchInput}
            onChange={table.setSearchInput}
            placeholder={t('cartera.facturas.searchPlaceholder')}
            data-testid="cartera-facturas-buscar"
          />
          <FilterSelect
            value={estado}
            onChange={(v) => {
              setEstado(v)
              table.setPage(1)
            }}
            options={ESTADOS}
            optionLabel={(e) => t(`cartera.estado.${e}`)}
            ariaLabel={t('cartera.facturas.filtroEstado')}
            testId="cartera-facturas-filtro-estado"
          />
          <FilterSelect
            value={payerKind}
            onChange={(v) => {
              setPayerKind(v)
              table.setPage(1)
            }}
            options={PAYER_KINDS}
            optionLabel={(p) => t(`cartera.payerKind.${p}`)}
            ariaLabel={t('cartera.facturas.filtroPayer')}
            testId="cartera-facturas-filtro-payer"
          />
        </>
      }
    />
  )
}
